import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  Wallet,
  ChevronLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  CreditCard,
  Building,
  Clock,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import api from '@/services/api';

const DoctorWallet = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [walletData, setWalletData] = useState({
    balance: 0,
    pendingBalance: 0,
    totalEarnings: 0,
    thisMonth: 0,
    lastMonth: 0,
    growth: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchWalletData();
  }, [isAuthenticated, navigate]);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      // Fetch wallet data
      const [walletsRes, earningsRes, transactionsRes] = await Promise.all([
        api.get('/wallets').catch(() => ({ data: { data: [] } })),
        api.get('/earnings').catch(() => ({ data: { data: [] } })),
        api.get('/wallet_transactions', {
          params: {
            'orderBy': 'created_at',
            'sortedBy': 'desc',
            'limit': 20
          }
        }).catch(() => ({ data: { data: [] } })),
      ]);
      
      // Process wallets
      const wallets = walletsRes.data?.data || [];
      const primaryWallet = wallets[0] || {};
      
      // Process earnings
      const earnings = earningsRes.data?.data || [];
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      let thisMonthEarnings = 0;
      let lastMonthEarnings = 0;
      let totalEarnings = 0;
      
      earnings.forEach(earning => {
        const earnDate = new Date(earning.created_at);
        const amount = parseFloat(earning.doctor_earning || earning.amount || 0);
        totalEarnings += amount;
        
        if (earnDate.getMonth() === currentMonth && earnDate.getFullYear() === currentYear) {
          thisMonthEarnings += amount;
        } else if (
          (earnDate.getMonth() === currentMonth - 1 && earnDate.getFullYear() === currentYear) ||
          (currentMonth === 0 && earnDate.getMonth() === 11 && earnDate.getFullYear() === currentYear - 1)
        ) {
          lastMonthEarnings += amount;
        }
      });
      
      // Calculate growth
      const growth = lastMonthEarnings > 0 
        ? ((thisMonthEarnings - lastMonthEarnings) / lastMonthEarnings * 100).toFixed(1) 
        : 0;
      
      setWalletData({
        balance: parseFloat(primaryWallet.balance || 0),
        pendingBalance: parseFloat(primaryWallet.pending_balance || 0),
        totalEarnings,
        thisMonth: thisMonthEarnings,
        lastMonth: lastMonthEarnings,
        growth: parseFloat(growth),
      });
      
      // Process transactions
      const rawTransactions = transactionsRes.data?.data || [];
      const transformedTransactions = rawTransactions.map(tx => ({
        id: tx.id,
        type: tx.action === 'credit' ? 'earning' : 'withdrawal',
        amount: parseFloat(tx.amount || 0),
        description: tx.description || (tx.action === 'credit' 
          ? (lang === 'ar' ? 'إيداع' : 'Credit')
          : (lang === 'ar' ? 'سحب' : 'Withdrawal')),
        date: new Date(tx.created_at),
        status: tx.status || 'completed',
      }));
      
      setTransactions(transformedTransactions);
      
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات المحفظة' : 'Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0 || amount > walletData.balance) {
      toast.error(lang === 'ar' ? 'مبلغ غير صحيح' : 'Invalid amount');
      return;
    }
    
    try {
      // API call to request withdrawal
      await api.post('/wallets', {
        amount: amount,
        action: 'withdrawal'
      });
      
      toast.success(lang === 'ar' ? 'تم طلب السحب بنجاح' : 'Withdrawal requested successfully');
      setWithdrawOpen(false);
      setWithdrawAmount('');
      fetchWalletData(); // Refresh data
    } catch (error) {
      console.error('Withdrawal failed:', error);
      toast.error(lang === 'ar' ? 'فشل في طلب السحب' : 'Withdrawal request failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(lang === 'ar' ? 'ar-SY' : 'en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-wallet">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/doctor/dashboard')}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{lang === 'ar' ? 'الأرباح والمدفوعات' : 'Earnings & Payments'}</h1>
                <p className="text-teal-100">{lang === 'ar' ? 'إدارة أرباحك ومدفوعاتك' : 'Manage your earnings and payments'}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="icon" 
              className="text-white border-white/30 hover:bg-white/10"
              onClick={fetchWalletData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Balance Card */}
          {loading ? (
            <Card className="bg-white/10 border-0">
              <CardContent className="p-6">
                <Skeleton className="h-16 w-48 bg-white/20" />
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/10 border-0">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <p className="text-teal-100 text-sm">{lang === 'ar' ? 'الرصيد المتاح' : 'Available Balance'}</p>
                    <p className="text-4xl font-bold mt-1">{formatCurrency(walletData.balance)} <span className="text-lg">{lang === 'ar' ? 'ل.س' : 'SYP'}</span></p>
                    {walletData.pendingBalance > 0 && (
                      <p className="text-teal-200 text-sm mt-2 flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {lang === 'ar' ? 'معلق:' : 'Pending:'} {formatCurrency(walletData.pendingBalance)} {lang === 'ar' ? 'ل.س' : 'SYP'}
                      </p>
                    )}
                  </div>
                  <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-white text-teal-700 hover:bg-teal-50" disabled={walletData.balance <= 0}>
                        <ArrowUpRight className="h-4 w-4 mr-2" />
                        {lang === 'ar' ? 'سحب الأرباح' : 'Withdraw'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="dark:bg-slate-800">
                      <DialogHeader>
                        <DialogTitle>{lang === 'ar' ? 'سحب الأرباح' : 'Withdraw Earnings'}</DialogTitle>
                        <DialogDescription>
                          {lang === 'ar' 
                            ? `الرصيد المتاح: ${formatCurrency(walletData.balance)} ل.س`
                            : `Available balance: ${formatCurrency(walletData.balance)} SYP`}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="py-4">
                        <Label>{lang === 'ar' ? 'المبلغ' : 'Amount'}</Label>
                        <Input 
                          type="number" 
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          placeholder={lang === 'ar' ? 'أدخل المبلغ' : 'Enter amount'}
                          className="mt-2"
                        />
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setWithdrawOpen(false)}>
                          {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                        </Button>
                        <Button onClick={handleWithdraw} className="bg-teal-600 hover:bg-teal-700">
                          {lang === 'ar' ? 'تأكيد السحب' : 'Confirm Withdrawal'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <DollarSign className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'إجمالي الأرباح' : 'Total Earnings'}</p>
                  {loading ? (
                    <Skeleton className="h-6 w-20" />
                  ) : (
                    <p className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(walletData.totalEarnings)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'هذا الشهر' : 'This Month'}</p>
                  {loading ? (
                    <Skeleton className="h-6 w-20" />
                  ) : (
                    <p className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(walletData.thisMonth)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الشهر الماضي' : 'Last Month'}</p>
                  {loading ? (
                    <Skeleton className="h-6 w-20" />
                  ) : (
                    <p className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(walletData.lastMonth)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dark:bg-slate-800 border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${walletData.growth >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                  {walletData.growth >= 0 
                    ? <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                    : <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                  }
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'النمو' : 'Growth'}</p>
                  {loading ? (
                    <Skeleton className="h-6 w-20" />
                  ) : (
                    <p className={`text-lg font-bold ${walletData.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {walletData.growth >= 0 ? '+' : ''}{walletData.growth}%
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Transactions */}
        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              {lang === 'ar' ? 'سجل المعاملات' : 'Transaction History'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="all">{lang === 'ar' ? 'الكل' : 'All'}</TabsTrigger>
                <TabsTrigger value="earnings">{lang === 'ar' ? 'الأرباح' : 'Earnings'}</TabsTrigger>
                <TabsTrigger value="withdrawals">{lang === 'ar' ? 'السحوبات' : 'Withdrawals'}</TabsTrigger>
              </TabsList>

              {['all', 'earnings', 'withdrawals'].map(tab => (
                <TabsContent key={tab} value={tab}>
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-center justify-between p-4 border dark:border-slate-700 rounded-lg">
                          <div className="flex items-center gap-4">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div>
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-24 mt-2" />
                            </div>
                          </div>
                          <Skeleton className="h-6 w-20" />
                        </div>
                      ))}
                    </div>
                  ) : transactions.filter(tx => 
                      tab === 'all' || 
                      (tab === 'earnings' && tx.type === 'earning') || 
                      (tab === 'withdrawals' && tx.type === 'withdrawal')
                    ).length === 0 ? (
                    <div className="text-center py-12">
                      <Wallet className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                      <p className="text-slate-500 dark:text-slate-400">
                        {lang === 'ar' ? 'لا توجد معاملات' : 'No transactions'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions
                        .filter(tx => 
                          tab === 'all' || 
                          (tab === 'earnings' && tx.type === 'earning') || 
                          (tab === 'withdrawals' && tx.type === 'withdrawal')
                        )
                        .map(tx => (
                          <div 
                            key={tx.id} 
                            className="flex items-center justify-between p-4 border dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                            data-testid={`transaction-${tx.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`p-2 rounded-full ${
                                tx.type === 'earning' 
                                  ? 'bg-green-100 dark:bg-green-900/30' 
                                  : 'bg-blue-100 dark:bg-blue-900/30'
                              }`}>
                                {tx.type === 'earning' 
                                  ? <ArrowDownLeft className="h-5 w-5 text-green-600 dark:text-green-400" /> 
                                  : <ArrowUpRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                }
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{tx.description}</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400">{format(tx.date, 'PPp', { locale })}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${tx.type === 'earning' ? 'text-green-600' : 'text-blue-600'}`}>
                                {tx.type === 'earning' ? '+' : '-'}{formatCurrency(tx.amount)} {lang === 'ar' ? 'ل.س' : 'SYP'}
                              </p>
                              <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className="mt-1">
                                {tx.status === 'completed' 
                                  ? (lang === 'ar' ? 'مكتمل' : 'Completed')
                                  : (lang === 'ar' ? 'معلق' : 'Pending')
                                }
                              </Badge>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorWallet;
